import { useEffect, useState } from "react";
import { ShieldCheck, ShieldAlert, Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, errorText } from "@/lib/api";
import { ManualEntryDialog } from "@/components/doorman/ManualEntryDialog";
import { LedgerTable } from "@/components/doorman/LedgerTable";
import { LedgerFilters, EMPTY_FILTERS, toParams } from "@/components/doorman/LedgerFilters";
import { EntryDetailSheet } from "@/components/doorman/EntryDetailSheet";

const PAGE_SIZE = 50;

export const LedgerPanel = ({ tick, onChange }) => {
  const [data, setData] = useState({ items: [], total: 0 });
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [verify, setVerify] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => { setPage(1); }, [filters]);

  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/entries", { params: { ...toParams(filters), page, page_size: PAGE_SIZE } }).then((r) => setData(r.data)).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [filters, page, tick]);

  const runVerify = async () => {
    setVerifying(true);
    try {
      const { data } = await api.get("/verify");
      setVerify(data);
      data.valid ? toast.success(`Chain intact · ${data.checked} entries checked`) : toast.error(`Chain broken at entry #${data.broken_at}`);
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setVerifying(false);
    }
  };

  const pages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));
  const from = data.total ? (page - 1) * PAGE_SIZE + 1 : 0;
  const to = Math.min(page * PAGE_SIZE, data.total);

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap gap-3 justify-end">
        <Button variant="outline" onClick={runVerify} disabled={verifying} data-testid="chain-verify-execute-btn" className="border-[#222D3D] bg-transparent">
          {verifying ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ShieldCheck className="h-4 w-4 mr-2" />}Verify chain
        </Button>
        <ManualEntryDialog onCreated={onChange} />
      </div>
      <LedgerFilters filters={filters} onChange={setFilters} tick={tick} total={data.total} />
      {verify && <VerifyBanner v={verify} />}
      <LedgerTable rows={data.items} onSelect={setSelected} />
      <EntryDetailSheet entry={selected} onClose={() => setSelected(null)} />
      <div className="flex items-center justify-between text-sm text-slate-400" data-testid="ledger-pagination">
        <span data-testid="ledger-result-count">{data.total ? `Showing ${from.toLocaleString()}–${to.toLocaleString()} of ${data.total.toLocaleString()}` : "No matching arrivals"}</span>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" className="border-[#222D3D] bg-transparent" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="ledger-prev-page"><ChevronLeft className="h-4 w-4" /></Button>
          <span className="font-mono text-xs" data-testid="ledger-page-indicator">{page} / {pages}</span>
          <Button size="sm" variant="outline" className="border-[#222D3D] bg-transparent" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="ledger-next-page"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>
    </section>
  );
};

const VerifyBanner = ({ v }) => (
  <div data-testid="chain-verification-status-badge"
    className={`rounded-md border px-4 py-3 text-sm flex items-start gap-3 ${v.valid ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300" : "border-rose-500/30 bg-rose-500/10 text-rose-300"}`}>
    {v.valid ? <ShieldCheck className="h-4 w-4 mt-0.5" /> : <ShieldAlert className="h-4 w-4 mt-0.5" />}
    <div>
      {v.valid ? `Chain intact — all ${v.checked} entries recomputed and linked correctly.` : `Chain broken at entry #${v.broken_at}: ${v.reason}.`}
      {v.head_hash && <p className="font-mono text-xs text-slate-400 mt-1 break-all">head {v.head_hash}</p>}
    </div>
  </div>
);
